import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import Modal from "../../../shared/components/Modal";
import { useAppContext } from "../../../shared/functions/Context";
import MeasureUpdateModal from "../../dialogs/measure-update/MeasureUpdateModal";
import MODAL_NAMES from "../../dialogs/ModalName";
import StrategicMapObjectiveModal from "../../dialogs/strategic-map-objective/StrategicMapObjectiveModal";
import MeasureItem from "./MeasureItem";
import ObjectiveItem from "./ObjectiveItem";
import Rating, { BarRating } from "../../shared/components/rating/Rating";
import Objective, { IObjective } from "../../../shared/models/Objective";
import { IMeasure } from "../../../shared/models/Measure";
import { totalFinalIndividualObjectiveRating } from "../../shared/functions/Scorecard";
import { useNavigate } from "react-router-dom";
import "./IndividualDashboard.scss";
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary";

const MetricsAnalytics = observer(() => {
  const { store } = useAppContext();

  const objectives = store.objective.allMe;
  const measures = store.measure.allMe;
  const redMeasures = measures.filter((measure) => measure.asJson.finalRating || measure.asJson.autoRating <= 2);


  const get_measures = (objective: Objective): IMeasure[] => {
    return measures.filter((measure) => measure.asJson.objective === objective.asJson.id).map((measure) => measure.asJson)
  };

  const midterm_rate = useMemo(() => {
    let _sum: number = 0;
    let final_rating: number[] = [];

    for (const objective of objectives) {
      const weight = objective.asJson.weight ? objective.asJson.weight / 100 : 0;

      const _measures = get_measures(objective).map((measure) => ({
        rating: measure.midtermRating || measure.midtermAutoRating,
      }));

      let _avg_rates = _measures.reduce((acc, curr) => {
        return acc + curr.rating;
      }, 0) / _measures.length;

      _avg_rates = (weight * Math.round(_avg_rates * 10)) / 10;

      final_rating.push(_avg_rates);
      _sum = final_rating.reduce((acc, value) => {
        return acc + value;
      }, 0);
    }
    return _sum;
  }, [objectives, measures]);

  const final_rate = useMemo(() => {
    let _sum: number = 0;
    let _rating: number[] = [];

    for (const objective of objectives) {
      const weight = objective.asJson.weight ? objective.asJson.weight / 100 : 0;
      const _measures = get_measures(objective).map((measure) => ({
        rating: measure.finalRating || measure.autoRating,
      }));

      let _avg_rates = _measures.reduce((acc, curr) => {
        return acc + curr.rating;
      }, 0) / _measures.length;

      _avg_rates = (weight * Math.round(_avg_rates * 10)) / 10;

      _rating.push(_avg_rates);
      _sum = _rating.reduce((acc, value) => {
        return acc + value;
      }, 0);
    }
    return _sum;
  }, [objectives, measures]);

  const midtermCss = midterm_rate <= 2 ? "warning" : "primary";
  const finalCss = final_rate <= 2 ? "warning" : "success";

  return (
    <div>
      <div
        className="uk-grid-small uk-grid-match uk-child-width-1-3@s uk-child-width-1-3@m uk-margin"
        data-uk-grid
        style={{ marginBottom: "30px" }}
      >
        <div>
          <div className="info-card info-card--primary  uk-card uk-card-default uk-card-small">
            <div
              className="icon"
              data-tooltip="View all measures in my scorecard"
            >
              <span>✓</span>
            </div>
            <div className="info-body uk-card-body">
              <p className="value">{measures.length}</p>
              <p className="label">All KPIs</p>
            </div>
          </div>
        </div>
        <div>
          <div className="info-card info-card--danger  uk-card uk-card-default uk-card-small">
            <div
              className="icon"
              data-tooltip="A red measures dashboard focuses on poorly performing metrics"
            >
              <span>❗</span>
            </div>
            <div className="info-body uk-card-body">
              <p className="value">{redMeasures.length}</p>
              <p className="label">Red KPIs</p>
            </div>
          </div>
        </div>
        <div>
          <div className="info-card info-card--success  uk-card uk-card-default uk-card-small">
            <div
              className="icon"
              data-tooltip="View all objectives in my corecard"
            >
              <span>✓</span>
            </div>
            <div className="info-body uk-card-body">
              <p className="value">{objectives.length}</p>
              <p className="label">Objectives</p>
            </div>
          </div>
        </div>
      </div>
      <div className="uk-width-1-1">
        <div className="objectives-card uk-card uk-card-default uk-card-body uk-card-small">
          <h5 className="title uk-margin">Quaterly Scores ✓</h5>
          <div className="uk-grid-small uk-child-width-1-4@s uk-child-width-1-4@m uk-margin" data-uk-grid style={{ marginBottom: "30px" }}>
            <div>
              <div
                className={`info-card info-card--${midtermCss}  uk-card uk-card-default uk-card-small`}
              >
                <div
                  className="icon"
                  data-tooltip="Average midterm score of my scorecard"
                >
                  {midtermCss === "primary" ? <span>✓</span> : <span>❗</span>}
                </div>
                <div className="info-body uk-card-body">
                  <p className="value">{midterm_rate.toFixed(2)}</p>
                  <p className="label">Midterm</p>
                </div>
              </div>
            </div>
            <div>
              <div
                className={`info-card info-card--${finalCss}  uk-card uk-card-default uk-card-small`}
              >
                <div
                  className="icon"
                  data-tooltip="Average final score of my scorecard"
                >
                  {finalCss === "success" ? <span>✓</span> : <span>❗</span>}
                </div>
                <div className="info-body uk-card-body">
                  <p className="value">{final_rate.toFixed(2)}</p>
                  <p className="label">Assessment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

interface INoDataProps {
  message: string;
  children?: React.ReactNode;
}
const NoData = (props: INoDataProps) => {
  return (
    <div className="uk-margin-top uk-text-center">
      <div className="uk-card uk-card-body">
        <p className="uk-text-center">
          {props.message}
          {props.children && <br />}
          {props.children && <br />}
          {props.children}
        </p>
      </div>
    </div>
  );
};

const ObjectivesAnalytics = () => {
  const [viewType, setViewType] = useState<"table" | "grid">("grid");

  return (
    <div
      className="uk-grid-small uk-child-width-1-3@m uk-margin uk-grid-match"
      data-uk-grid
      style={{ marginBottom: "30px" }}
    >
      <div className="uk-width-1-1">
        <div className="objectives-card uk-card uk-card-default uk-card-body uk-card-small">
          <div className="objective-analytics-toolbar">
            <h5 className="title uk-margin">Objectives ✓</h5>

            <div
              className="controls"
              style={{
                display: "flex",
              }}
            >
              <button
                className={
                  "list-btn btn-icon uk-margin-small-right " +
                  (viewType === "grid" ? "active" : "")
                }
                onClick={() => setViewType("grid")}
              >
                <span data-uk-icon="icon: grid"></span>
              </button>

              <button
                className={
                  "list-btn btn-icon " + (viewType === "table" ? "active" : "")
                }
                onClick={() => setViewType("table")}
              >
                <span data-uk-icon="icon: table"></span>
              </button>
            </div>
          </div>

          {viewType === "table" && <ObjectivesTable />}
          {viewType === "grid" && <ObjectivesGrid />}
        </div>
      </div>
    </div>
  );
};

const ObjectivesTable = observer(() => {
  const { store } = useAppContext();
  const objectives = store.objective.allMe;
  const navigate = useNavigate();

  return (
    <ul className="uk-list uk-list-striped uk-margin">
      {objectives.map((objective) => (
        <ErrorBoundary key={objective.asJson.id}>
          <ObjectiveItem objective={objective} />
        </ErrorBoundary>
      ))}

      {objectives.length === 0 && (
        <NoData message="No data to display. Please add a new objective.">
          <button
            className="btn btn-primary uk-button-small"
            onClick={() => navigate("/c/scorecards/my")}
          >
            Add a new objective
          </button>
        </NoData>
      )}
    </ul>
  );
});

const ObjectivesGrid = observer(() => {
  const { store } = useAppContext();
  const objectives = store.objective.allMe;
  const measures = store.measure.allMe;

  // calculate rating
  const calculateRating = (objective: IObjective) => {
    const measures = getMeasures(objective);
    const rating = totalFinalIndividualObjectiveRating(measures);
    return rating || 1;
  };

  const getMeasures = (objective: IObjective): IMeasure[] => {
    return measures.filter((measure) => measure.asJson.objective === objective.id).map((measure) => measure.asJson);
  };

  return (
    <div
      className="uk-grid-small uk-child-width-1-3@m uk-child-width-1-4@l uk-grid-match uk-margin"
      data-uk-grid
      style={{ marginBottom: "30px" }}
    >
      {objectives.map((objective) => (
        <div key={objective.asJson.id}>
          <div className="score uk-card uk-card-default uk-card-body uk-card-small">
            <h5 className="sub-heading">{objective.asJson.description}</h5>
            <ErrorBoundary>
              <div
                className="uk-grid-small uk-child-width-1-2 uk-grid-match uk-margin"
                data-uk-grid
                style={{ marginBottom: "30px" }}
              >
                <div>
                  <div className="rating-container">
                    <BarRating rating={calculateRating(objective.asJson)} />
                  </div>
                </div>
                <div>
                  <div className="rating-container">
                    <Rating rate={objective.rating.rate}
                      isUpdated={objective.rating.isUpdated}
                    />
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </div>
        </div>
      ))}
    </div>
  );
});

const MeasuresAnalytics = observer(() => {
  const { store } = useAppContext();
  const measures = store.measure.allMe;

  const redMeasures = measures.filter((measure) => measure.asJson.finalRating! < 2);
  const greenMeasures = measures.filter((measure) => measure.asJson.finalRating! >= 3);
  const amberMeasures = measures.filter((measure) => measure.asJson.finalRating! < 3 && measure.asJson.finalRating! >= 2);

  return (
    <div
      className="uk-grid-small uk-child-width-1-3@m uk-margin uk-grid-match"
      data-uk-grid
      style={{ marginBottom: "30px" }}
    >
      <div>
        <div className="red-measures-card uk-card uk-card-default uk-card-body uk-card-small">
          <h5 className="title uk-margin">Green KPIs/Measures 🙂</h5>
          <ul className="uk-list uk-list-striped uk-margin">
            {greenMeasures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureItem measure={measure.asJson} />
              </ErrorBoundary>
            ))}
            {greenMeasures.length === 0 && (
              <NoData message="No data to display. " />
            )}
          </ul>
        </div>
      </div>

      <div>
        <div className="red-measures-card uk-card uk-card-default uk-card-body uk-card-small">
          <h5 className="title uk-margin">Amber KPIs/Measures 😐</h5>
          <ul className="uk-list uk-list-striped uk-margin">
            {amberMeasures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureItem measure={measure.asJson} />
              </ErrorBoundary>
            ))}
            {amberMeasures.length === 0 && (
              <NoData message="No data to display. " />
            )}
          </ul>
        </div>
      </div>
      <div>
        <div className="red-measures-card uk-card uk-card-default uk-card-body uk-card-small">
          <h5 className="title uk-margin">All Red KPIs/Measures 😔</h5>
          <ul className="uk-list uk-list-striped uk-margin">
            {redMeasures.map((measure) => (
              <ErrorBoundary key={measure.asJson.id}>
                <MeasureItem measure={measure.asJson} />
              </ErrorBoundary>
            ))}
            {redMeasures.length === 0 && (
              <NoData message="No red measures 🙂." />
            )}
          </ul>
        </div>
      </div>
    </div>
  );
});

const IndividualDashboard = () => {
  return (
    <>
      <div className="individual-dashboard">
        {/* Info cards */}
        <MetricsAnalytics />
        <ObjectivesAnalytics />
        <MeasuresAnalytics />
      </div>

      {/* Modals */}
      <Modal modalId={MODAL_NAMES.EXECUTION.MEASURE_UPDATE_MODAL}>
        <MeasureUpdateModal />
      </Modal>

      <Modal
        modalId={MODAL_NAMES.EXECUTION.MAP_OVERVIEW_MODAL}
        cssClass="uk-modal-container"
      >
        <StrategicMapObjectiveModal />
      </Modal>
    </>
  );
};

export default IndividualDashboard;
